<template>
  <div>
    <v-card class="rounded-lg elevation-3">
      <v-sheet color="primary" dark class="pa-4 d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <v-icon size="30" class="mr-3">mdi-account-cog</v-icon>
          <h2 class="text-h5 mb-0">Quản lý người dùng</h2>
        </div>
        <v-btn color="white" class="primary--text" @click="openCreate">
          <v-icon left>mdi-account-plus</v-icon>Thêm người dùng
        </v-btn>
      </v-sheet>

      <v-card-title>
        <v-row>
          <v-col cols="12" md="5">
            <v-text-field v-model="filters.search" label="Tên, email hoặc số điện thoại"
              prepend-inner-icon="mdi-magnify" outlined dense clearable hide-details @keyup.enter="loadUsers(1)" />
          </v-col>
          <v-col cols="12" md="3">
            <v-select v-model="filters.type" :items="roles" label="Vai trò" outlined dense clearable hide-details
              @change="loadUsers(1)" />
          </v-col>
          <v-col cols="12" md="3">
            <v-select v-model="filters.is_active" :items="statuses" label="Trạng thái" outlined dense clearable
              hide-details @change="loadUsers(1)" />
          </v-col>
          <v-col cols="12" md="1">
            <v-btn icon color="primary" @click="loadUsers(1)"><v-icon>mdi-filter-variant</v-icon></v-btn>
          </v-col>
        </v-row>
      </v-card-title>

      <v-data-table :headers="headers" :items="users" :loading="loading" :server-items-length="total"
        :page.sync="page" :items-per-page.sync="perPage" @update:page="loadUsers" @update:items-per-page="loadUsers(1)">
        <template v-slot:item.type="{ item }">
          <v-chip small :color="item.type === 1 ? 'primary' : 'teal'" dark>{{ roleName(item.type) }}</v-chip>
        </template>
        <template v-slot:item.is_active="{ item }">
          <v-chip small :color="item.is_active ? 'success' : 'grey'" dark>
            {{ item.is_active ? 'Đang hoạt động' : 'Đã khóa' }}
          </v-chip>
        </template>
        <template v-slot:item.updated_at="{ item }">{{ formatDate(item.updated_at) }}</template>
        <template v-slot:item.actions="{ item }">
          <v-btn icon small color="primary" @click="openEdit(item)"><v-icon small>mdi-pencil</v-icon></v-btn>
          <v-btn icon small :color="item.is_active ? 'error' : 'success'" :disabled="item.id === currentUserId"
            @click="toggleUser(item)">
            <v-icon small>{{ item.is_active ? 'mdi-lock' : 'mdi-lock-open' }}</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="dialog" max-width="650" persistent>
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon dark class="mr-2">mdi-account-edit</v-icon>{{ editingId ? 'Cập nhật người dùng' : 'Thêm người dùng' }}
        </v-card-title>
        <v-card-text class="pt-6">
          <v-form ref="form" v-model="valid">
            <v-row>
              <v-col cols="12" md="6"><v-text-field v-model="form.name" label="Họ tên *" outlined dense
                :rules="[required]" /></v-col>
              <v-col cols="12" md="6"><v-text-field v-model="form.email" label="Email *" type="email" outlined dense
                :rules="[required]" /></v-col>
              <v-col cols="12" md="6"><v-text-field v-model="form.phone" label="Số điện thoại" outlined dense /></v-col>
              <v-col cols="12" md="6"><v-select v-model="form.type" :items="roles" label="Vai trò *" outlined dense
                :rules="[required]" /></v-col>
              <v-col cols="12"><v-text-field v-model="form.address" label="Địa chỉ" outlined dense /></v-col>
              <v-col cols="12"><v-text-field v-model="form.password" :label="editingId ? 'Mật khẩu mới (để trống nếu không đổi)' : 'Mật khẩu *'"
                type="password" outlined dense :rules="editingId ? [] : [required, minPassword]" /></v-col>
              <v-col cols="12"><v-switch v-model="form.is_active" label="Tài khoản hoạt động" color="success" /></v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions class="px-6 pb-5">
          <v-spacer />
          <v-btn text @click="dialog = false">Hủy</v-btn>
          <v-btn color="primary" :loading="saving" @click="save">Lưu</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import userManagement from '@/api/userManagement'

const emptyForm = () => ({ name: '', email: '', phone: '', address: '', password: '', type: 3, is_active: true })

export default {
  data() {
    return {
      users: [], loading: false, saving: false, dialog: false, valid: false, editingId: null,
      page: 1, perPage: 10, total: 0, form: emptyForm(),
      filters: { search: '', type: null, is_active: null },
      roles: [{ text: 'Admin', value: 1 }, { text: 'Sale', value: 3 }],
      statuses: [{ text: 'Đang hoạt động', value: 1 }, { text: 'Đã khóa', value: 0 }],
      headers: [
        { text: 'Họ tên', value: 'name' }, { text: 'Email', value: 'email' },
        { text: 'Số điện thoại', value: 'phone' }, { text: 'Vai trò', value: 'type' },
        { text: 'Trạng thái', value: 'is_active' }, { text: 'Cập nhật', value: 'updated_at' },
        { text: '', value: 'actions', sortable: false, width: 100 }
      ],
      required: value => !!value || 'Trường này là bắt buộc',
      minPassword: value => String(value || '').length >= 6 || 'Mật khẩu tối thiểu 6 ký tự'
    }
  },
  computed: {
    currentUserId() {
      const user = JSON.parse(localStorage.getItem('user_info') || '{}')
      return Number(user.id || user.user_id || 0)
    }
  },
  created() { this.loadUsers() },
  methods: {
    async loadUsers(page = this.page) {
      this.loading = true; this.page = page
      try {
        const response = await userManagement.list({ ...this.filters, page, per_page: this.perPage })
        this.users = response.data.data || []
        this.total = response.data.total || 0
      } catch (error) { this.showError(error, 'Không thể tải danh sách người dùng') }
      finally { this.loading = false }
    },
    openCreate() { this.editingId = null; this.form = emptyForm(); this.dialog = true; this.$nextTick(() => this.$refs.form?.resetValidation()) },
    openEdit(item) { this.editingId = item.id; this.form = { ...emptyForm(), ...item, password: '' }; this.dialog = true; this.$nextTick(() => this.$refs.form?.resetValidation()) },
    async save() {
      if (!this.$refs.form.validate()) return
      this.saving = true
      try {
        if (this.editingId) await userManagement.update(this.editingId, this.form)
        else await userManagement.create(this.form)
        this.$snackbar.success(this.editingId ? 'Cập nhật người dùng thành công' : 'Tạo người dùng thành công')
        this.dialog = false; await this.loadUsers()
      } catch (error) { this.showError(error, 'Không thể lưu người dùng') }
      finally { this.saving = false }
    },
    async toggleUser(item) {
      try {
        await userManagement.toggle(item.id)
        this.$snackbar.success(item.is_active ? 'Đã khóa tài khoản' : 'Đã mở tài khoản')
        await this.loadUsers()
      } catch (error) { this.showError(error, 'Không thể thay đổi trạng thái tài khoản') }
    },
    roleName(type) { return Number(type) === 1 ? 'Admin' : 'Sale' },
    formatDate(value) { return value ? new Date(value).toLocaleString('vi-VN') : '' },
    showError(error, fallback) {
      const errors = error.response?.data?.errors
      const message = error.response?.data?.message || (errors && Object.values(errors)[0]?.[0]) || fallback
      this.$snackbar.error(message)
    }
  }
}
</script>
